import db, { createRef } from "src/db";
import type { IMarketplaceAccountService } from "./IMarketplaceAccountService";
import { MARKETPLACE_TYPES } from "../types/marketplaceAccount";
import type { MarketplaceAccount, MarketplaceListRequest, MarketplaceListResult } from "../types/marketplaceAccount";

const supportedMarketplaceTypes = new Set<string>(MARKETPLACE_TYPES);

const normalizeText = (value: unknown) => String(value || "").toLocaleLowerCase();

const getTimestampValue = (value: unknown) => {
  if (value && typeof (value as { toMillis?: () => number }).toMillis === "function") {
    return (value as { toMillis: () => number }).toMillis();
  }
  if (value instanceof Date) return value.getTime();
  return 0;
};

class FirestoreMarketplaceAccountService implements IMarketplaceAccountService {
  async getAccount(accountId: string): Promise<MarketplaceAccount | null> {
    const snapshot = await db.collection("account").doc(accountId).get();
    return snapshot.exists ? ({ ...snapshot.data(), id: snapshot.id } as MarketplaceAccount) : null;
  }

  async listAccounts(request: MarketplaceListRequest): Promise<MarketplaceListResult> {
    const snapshot = await db
      .collection("account")
      .where("client", "==", createRef("clients", request.clientId))
      .get();

    const search = normalizeText(request.search);
    const filtered = snapshot.docs
      .map((item) => ({ ...item.data(), id: item.id }) as MarketplaceAccount)
      .filter((account) => supportedMarketplaceTypes.has(account.type))
      .filter((account) => request.activity === "all" || Boolean(account.isActive) === (request.activity === "active"))
      .filter((account) => request.marketplaceTypes.length === 0 || request.marketplaceTypes.includes(account.type))
      .filter((account) => {
        if (!search) return true;
        return [account.name, account.searchKey, account.marketplaceShopId, account.type]
          .some((value) => normalizeText(value).includes(search));
      });

    filtered.sort((left, right) => {
      const leftValue = request.sort === "updatedAt"
        ? getTimestampValue(left.updatedAt || left.createdAt)
        : normalizeText(left[request.sort]);
      const rightValue = request.sort === "updatedAt"
        ? getTimestampValue(right.updatedAt || right.createdAt)
        : normalizeText(right[request.sort]);
      const comparison = leftValue < rightValue ? -1 : leftValue > rightValue ? 1 : 0;
      return request.sortDirection === "asc" ? comparison : -comparison;
    });

    const start = (request.page - 1) * request.pageSize;
    return { items: filtered.slice(start, start + request.pageSize), total: filtered.length };
  }
}

export const firestoreMarketplaceAccountService = new FirestoreMarketplaceAccountService();
